import { useMutation } from '@apollo/client/react';
import { type FC, type ReactNode, useState } from 'react';

import EditCreateModal from '@zen/Components/EditCreateModal';
import type { CreateEditModalConfiguration } from '@zen/Components/EditCreateModal/types';
import { Button, Table } from '@zen/DesignSystem';
import { type SortInput } from '@zen/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import { performMutation } from '@zen/utils/performMutation';

import { ProductAttributesTrackingAction, ProductAttributesTrackingCategory } from '../types';
import CreateProductAttribute from './CreateProductAttribute';
import EditProductAttribute from './EditProductAttribute';
import {
  DeleteProductAttributeDocument,
  type DeleteProductAttributeMutationResult,
  type DeleteProductAttributeMutationVariables
} from './graphql';
import useProductAttributes from './hooks/useProductAttributes';
import { getColumns } from './productAttributes.tableConfiguration';
import { type CoreProductAttribute } from './types';

interface Props {
  accountId: string;
  canCreateProductAttribute: boolean;
}

const ProductAttributes: FC<Props> = ({ accountId, canCreateProductAttribute }) => {
  const [order, setOrder] = useState<SortInput | undefined>();
  const { loading, error, nodes, paginationInfo, totalCount } = useProductAttributes(accountId, order);
  const { addError, addSuccess } = useNotification();
  const { trackEvent } = useTracking();
  const [deleteProductAttribute] = useMutation<DeleteProductAttributeMutationResult, DeleteProductAttributeMutationVariables>(
    DeleteProductAttributeDocument
  );

  const [modalConfiguration, setModalConfiguration] = useState<CreateEditModalConfiguration<CoreProductAttribute>>();

  const handleAdd = (): void => {
    setModalConfiguration({
      type: 'create',
      title: 'Create attribute type',
      modalOverflowY: 'auto'
    });
  };

  const handleEdit = (attribute: CoreProductAttribute): void => {
    setModalConfiguration({
      type: 'edit',
      initialValues: attribute,
      title: 'Edit attribute type',
      modalOverflowY: 'auto'
    });
  };

  const trackAttributeDeletion = (id: string): void => {
    trackEvent({
      category: ProductAttributesTrackingCategory,
      action: ProductAttributesTrackingAction.ATTRIBUTE_DELETED,
      label: id,
      properties: { attributeId: id }
    });
  };

  const handleDelete = async (id: string): Promise<void> => {
    trackAttributeDeletion(id);

    await performMutation({
      mutationFn: () =>
        deleteProductAttribute({
          variables: {
            input: {
              id
            }
          },
          refetchQueries: ['productAttributes'],
          awaitRefetchQueries: true
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess('Product attribute has been deleted.');
      }
    });
  };

  const handleCloseModal = (): void => setModalConfiguration(undefined);

  const actions: ReactNode = canCreateProductAttribute ? (
    <Button onClick={handleAdd} size="compact">
      Create attribute type
    </Button>
  ) : undefined;

  return (
    <>
      <Table
        actions={actions}
        columns={getColumns({ onEdit: handleEdit, onDelete: handleDelete })}
        data={nodes}
        error={!!error}
        loading={loading}
        onOrderChange={setOrder}
        order={order}
        paginationInfo={paginationInfo}
        tableId="product-attributes"
        title="Attribute types"
        totalCountConfig={{
          entityName: 'attribute type',
          isLoading: loading,
          totalCount
        }}
      />
      <EditCreateModal
        additionalCreateFormProps={{ accountId }}
        configuration={modalConfiguration}
        createForm={CreateProductAttribute}
        editForm={EditProductAttribute}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProductAttributes;
